<template>
  <div class="pl-page">

    <!-- ─── Encabezado: barra sticky que se encoge al hacer scroll ─── -->
    <div class="pl-page-header" :class="{ 'pl-page-header--scrolled': headerScrolled }">
      <div class="pl-page-header-inner">
        <button class="pl-back-btn" aria-label="Regresar al Taller Creativo"
          @click="$router.push({ path: '/', hash: '#taller-creativo' })">
          <i class="fas fa-arrow-left"></i>
        </button>
        <div class="pl-title-area">
          <h1 class="pl-heading">Plantillas Imprimibles</h1>
          <p class="pl-desc">Crea hojas de trabajo, agendas y cuadernos personalizados listos para imprimir.</p>
        </div>
        <span class="pl-chip"><i class="fas fa-wand-magic"></i> Taller Creativo</span>
      </div>
    </div>

    <!-- ─── Cuerpo ─── -->
    <div class="pl-body-wrap">
    <div class="pl-page-body">

      <!-- Panel de Configuración -->
      <aside class="pl-aside">
        <div class="pl-panel-content">

          <!-- 0. Tamaño -->
          <div class="pl-section">
            <h2 class="pl-sec-head" @click="toggleSection('size')">
              Tamaño de Hoja <span class="pl-arrow" :class="{ up: !sectionOpen.size }">▼</span>
            </h2>
            <div class="pl-sec-body" v-show="sectionOpen.size">
              <div class="pl-ctrl">
                <select v-model="state.sheetPreset" @change="onPresetChange" class="pl-select">
                  <option value="cuaderno">Cuaderno 100×180mm</option>
                  <option value="a6">A6 105×148mm</option>
                  <option value="a5">A5 148×210mm</option>
                  <option value="a4">A4 210×297mm</option>
                  <option value="letter">Letter 216×279mm</option>
                  <option value="tarjeta">Tarjeta 85×55mm</option>
                  <option value="custom">Personalizado</option>
                </select>
              </div>
              <div v-if="state.sheetPreset === 'custom'" class="pl-mini-grid">
                <div class="pl-ctrl">
                  <label class="pl-label">Ancho (mm)</label>
                  <input type="number" v-model.number="state.sheetW" min="30" max="400" class="pl-input">
                </div>
                <div class="pl-ctrl">
                  <label class="pl-label">Alto (mm)</label>
                  <input type="number" v-model.number="state.sheetH" min="30" max="600" class="pl-input">
                </div>
              </div>
            </div>
          </div>

          <!-- 1. Estructura -->
          <div class="pl-section">
            <h2 class="pl-sec-head" @click="toggleSection('structure')">
              Estructura <span class="pl-arrow" :class="{ up: !sectionOpen.structure }">▼</span>
            </h2>
            <div class="pl-sec-body" v-show="sectionOpen.structure">
              <div class="pl-ctrl">
                <label class="pl-label">Espaciado <span class="pl-val">{{ state.spacing }}mm</span></label>
                <input type="number" v-model.number="state.spacing" min="4" step="0.5" class="pl-input">
              </div>
              <div class="pl-ctrl">
                <label class="pl-label">Grosor Líneas <span class="pl-val">{{ (state.lineThickness * 0.5).toFixed(1) }}px</span></label>
                <input type="range" v-model.number="state.lineThickness" min="1" max="6" step="1" class="pl-range">
              </div>
              <div class="pl-ctrl">
                <label class="pl-label">Color Líneas</label>
                <input type="color" v-model="state.lineColor" class="pl-color">
              </div>
              <label class="pl-sublabel">Borde Exterior</label>
              <div class="pl-mini-grid">
                <select v-model="state.gridBorderWidth" class="pl-select">
                  <option value="0px">Sin Borde</option>
                  <option value="1px">1px</option>
                  <option value="2px">2px</option>
                  <option value="4px">4px</option>
                </select>
                <select v-model="state.gridBorderStyle" class="pl-select">
                  <option value="solid">Sólido</option>
                  <option value="dashed">Guiones</option>
                  <option value="dotted">Puntos</option>
                </select>
              </div>
            </div>
          </div>

          <!-- 2. Márgenes y Fondo -->
          <div class="pl-section">
            <h2 class="pl-sec-head" @click="toggleSection('margins')">
              Márgenes y Fondo <span class="pl-arrow" :class="{ up: !sectionOpen.margins }">▼</span>
            </h2>
            <div class="pl-sec-body" v-show="sectionOpen.margins">
              <div class="pl-info-box">
                <div class="pl-info-row">
                  <label class="pl-label" style="margin:0">Fondo Margen</label>
                  <input type="color" v-model="state.marginColor" class="pl-color pl-color--sm">
                </div>
                <div class="pl-ctrl" style="margin-top:6px">
                  <label class="pl-label">Intensidad <span class="pl-val">{{ state.marginIntensity }}%</span></label>
                  <input type="range" v-model.number="state.marginIntensity" min="0" max="100" class="pl-range">
                </div>
              </div>
              <div class="pl-mini-grid" style="margin-top:10px">
                <div class="pl-ctrl">
                  <label class="pl-label">Sup. <span class="pl-val">{{ state.mTop }}mm</span></label>
                  <input type="range" v-model.number="state.mTop" min="0" max="25" class="pl-range">
                </div>
                <div class="pl-ctrl">
                  <label class="pl-label">Inf. <span class="pl-val">{{ state.mBot }}mm</span></label>
                  <input type="range" v-model.number="state.mBot" min="0" max="25" class="pl-range">
                </div>
              </div>
              <p class="pl-help">Izq/Der fijos a 10mm</p>
              <div class="pl-info-box" style="margin-top:8px">
                <div class="pl-ctrl">
                  <label class="pl-label">Desplazamiento <span class="pl-val">{{ state.gutterX > 0 ? '+' + state.gutterX : state.gutterX }}</span></label>
                  <input type="range" v-model.number="state.gutterX" min="-20" max="20" step="1" class="pl-range">
                </div>
              </div>
            </div>
          </div>

          <!-- 3. Encabezado -->
          <div class="pl-section">
            <h2 class="pl-sec-head" @click="toggleSection('header')">
              Encabezado <span class="pl-arrow" :class="{ up: !sectionOpen.header }">▼</span>
            </h2>
            <div class="pl-sec-body" v-show="sectionOpen.header">
              <div class="pl-ctrl">
                <label class="pl-label">Tamaño <span class="pl-val">{{ state.headHeight }}mm</span></label>
                <input type="range" v-model.number="state.headHeight" min="5" max="50" class="pl-range">
              </div>
              <div class="pl-mini-grid">
                <div class="pl-ctrl">
                  <label class="pl-label">Fondo</label>
                  <input type="color" v-model="state.headBg" class="pl-color">
                </div>
                <div class="pl-ctrl">
                  <label class="pl-label">Texto</label>
                  <input type="color" v-model="state.headTxt" class="pl-color">
                </div>
              </div>
              <div class="pl-ctrl">
                <label class="pl-label">Intensidad <span class="pl-val">{{ state.headIntensity }}%</span></label>
                <input type="range" v-model.number="state.headIntensity" min="0" max="100" class="pl-range">
              </div>
              <label class="pl-sublabel">Borde Separador</label>
              <div class="pl-mini-grid">
                <select v-model="state.headBorderWidth" class="pl-select">
                  <option value="0px">Sin Borde</option>
                  <option value="1px">1px</option>
                  <option value="2px">2px</option>
                  <option value="4px">4px</option>
                </select>
                <select v-model="state.headBorderStyle" class="pl-select">
                  <option value="solid">Sólido</option>
                  <option value="dashed">Guiones</option>
                  <option value="dotted">Puntos</option>
                </select>
              </div>
              <label class="pl-sublabel">Bordes Externos (T/L/R)</label>
              <div class="pl-mini-grid">
                <select v-model="state.headOuterWidth" class="pl-select">
                  <option value="0px">Sin Borde</option>
                  <option value="1px">1px</option>
                  <option value="2px">2px</option>
                  <option value="4px">4px</option>
                </select>
                <select v-model="state.headOuterStyle" class="pl-select">
                  <option value="solid">Sólido</option>
                  <option value="dashed">Guiones</option>
                  <option value="dotted">Puntos</option>
                </select>
              </div>
            </div>
          </div>

          <!-- 4. Imagen de Fondo -->
          <div class="pl-section">
            <h2 class="pl-sec-head" @click="toggleSection('bgImage')">
              Imagen de Fondo <span class="pl-arrow" :class="{ up: !sectionOpen.bgImage }">▼</span>
            </h2>
            <div class="pl-sec-body" v-show="sectionOpen.bgImage">
              <input type="file" accept="image/*" @change="handleImageUpload" class="pl-file-input">
              <div class="pl-ctrl">
                <label class="pl-label">Opacidad <span class="pl-val">{{ state.bgImageOpacity }}%</span></label>
                <input type="range" v-model.number="state.bgImageOpacity" min="0" max="100" class="pl-range">
              </div>
              <div class="pl-autofit-box">
                <label class="pl-check-label">
                  <input type="checkbox" v-model="state.bgImageAutoFit"> Auto-ajustar (Cover)
                </label>
              </div>
              <div :class="{ 'pl-disabled': state.bgImageAutoFit }">
                <div class="pl-ctrl">
                  <label class="pl-label">Tamaño <span class="pl-val">{{ state.bgImageSize === 4 ? '4mm' : state.bgImageSize + '%' }}</span></label>
                  <input type="range" v-model.number="state.bgImageSize" min="4" max="100" :disabled="state.bgImageAutoFit" class="pl-range">
                </div>
                <div class="pl-pos-grid">
                  <button v-for="p in BG_POSITIONS" :key="p.pos"
                    class="pl-pos-btn" :class="[p.cls, { active: state.bgImagePos === p.pos }]"
                    :disabled="state.bgImageAutoFit"
                    type="button"
                    @click="setBgPos(p.pos)"></button>
                </div>
              </div>
            </div>
          </div>

          <!-- 5. Columnas & Layout -->
          <div class="pl-section">
            <h2 class="pl-sec-head" @click="toggleSection('columns')">
              Columnas &amp; Layout <span class="pl-arrow" :class="{ up: !sectionOpen.columns }">▼</span>
            </h2>
            <div class="pl-sec-body" v-show="sectionOpen.columns">
              <div class="pl-ctrl">
                <label class="pl-label">Orientación</label>
                <select v-model="state.contentDir" class="pl-select">
                  <option value="column">Vertical (Normal)</option>
                  <option value="row">Horizontal (Rotado)</option>
                </select>
              </div>
              <div class="pl-col-list">
                <div v-for="(col, idx) in state.columns" :key="col.id"
                  class="pl-col-card"
                  :class="{ 'col-dragging': dragSrc === idx, 'col-over': dragOver === idx }"
                  draggable="true"
                  @dragstart="onDragStart(idx)"
                  @dragend="onDragEnd"
                  @dragover.prevent="onDragOver(idx)"
                  @drop.prevent="onDrop(idx)">
                  <div class="pl-col-head">
                    <span class="pl-drag-handle" title="Arrastrar para reordenar">⠿</span>
                    <input type="text" v-model="col.name" class="pl-input pl-input--flex">
                    <button class="pl-btn-del" @click="delCol(idx)" type="button">×</button>
                  </div>
                  <div class="pl-col-body-row">
                    <select v-model="col.type" class="pl-select">
                      <option value="pat-lines">Rayas</option>
                      <option value="pat-grid">Cuadrícula</option>
                      <option value="pat-dots">Puntos</option>
                      <option value="pat-check">Check Box</option>
                      <option value="pat-blank">Blanco</option>
                    </select>
                    <input type="number" v-model.number="col.width" min="5" max="200" class="pl-input pl-input--w60">
                  </div>
                </div>
              </div>
              <button v-if="state.columns.length < 5" class="pl-btn-add" @click="addCol" type="button">
                + Agregar Columna
              </button>
            </div>
          </div>

        </div><!-- /panel-content -->

        <!-- Sección de impresión -->
        <div class="pl-print-section">
          <button class="pl-btn-print" @click="doPrint" type="button">
            <i class="fas fa-print"></i> Imprimir
          </button>
        </div>
      </aside>

      <!-- Preview de la Hoja -->
      <main class="pl-main">
        <div class="pl-zoom-bar">
          <button class="pl-zoom-btn" type="button" @click="setZoom(state.previewZoom - 10)" :disabled="state.previewZoom <= 25" title="Reducir">−</button>
          <span class="pl-zoom-val">{{ state.previewZoom }}%</span>
          <button class="pl-zoom-btn" type="button" @click="setZoom(state.previewZoom + 10)" :disabled="state.previewZoom >= 200" title="Aumentar">+</button>
          <button class="pl-zoom-btn pl-zoom-reset" type="button" @click="setZoom(100)" title="Restablecer 100%"><i class="fas fa-expand"></i></button>
        </div>
        <div class="pl-sheet-scroll">
          <div class="pl-sheet-zoom" :style="zoomWrapStyle">
            <div ref="sheetRef" class="pl-sheet" :style="sheetCssVars">
              <div class="pl-write-area">
                <div class="pl-bg-img"></div>
                <div class="pl-grid-layout" :class="state.contentDir === 'column' ? 'is-col' : 'is-row'">
                  <div class="pl-row-header">
                    <div v-for="col in state.columns" :key="'h' + col.id"
                      class="pl-cell-head" :style="{ flex: col.width }">
                      {{ col.name }}
                    </div>
                  </div>
                  <div class="pl-row-body">
                    <div v-for="col in state.columns" :key="'b' + col.id"
                      class="pl-cell-body" :class="col.type" :style="{ flex: col.width }"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div><!-- /page-body -->
    </div><!-- /body-wrap -->

    <!-- Contenedor de impresión (oculto normalmente) -->
    <div ref="printMountRef" class="pl-print-mount"></div>

  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue'

const SHEET_SIZES = {
  cuaderno: { w: 100, h: 180 },
  a6:       { w: 105, h: 148 },
  a5:       { w: 148, h: 210 },
  a4:       { w: 210, h: 297 },
  letter:   { w: 216, h: 279 },
  tarjeta:  { w: 85,  h: 55  },
}

const BG_POSITIONS = [
  { cls: 'p-tl', pos: 'top left'     }, { cls: 'p-tc', pos: 'top center'    }, { cls: 'p-tr', pos: 'top right'     },
  { cls: 'p-cl', pos: 'center left'  }, { cls: 'p-cc', pos: 'center center' }, { cls: 'p-cr', pos: 'center right'  },
  { cls: 'p-bl', pos: 'bottom left'  }, { cls: 'p-bc', pos: 'bottom center' }, { cls: 'p-br', pos: 'bottom right'  },
]

let colIdCounter = 0
function makeCol(name, width, type) {
  return { id: ++colIdCounter, name, width, type }
}

export default {
  name: 'PlantillasImprimibles',
  setup() {
    const sheetRef     = ref(null)
    const printMountRef = ref(null)

    const sectionOpen = reactive({
      size: true, structure: true, margins: true,
      header: true, bgImage: false, columns: true,
    })

    const state = reactive({
      sheetPreset: 'cuaderno',
      sheetW: 100, sheetH: 180,
      spacing: 7,
      lineThickness: 2,
      lineColor: '#94a3b8',
      gridBorderWidth: '1px',
      gridBorderStyle: 'solid',
      marginColor: '#ffffff',
      marginIntensity: 100,
      mTop: 10, mBot: 10, gutterX: 0,
      headHeight: 15,
      headBg: '#e2e8f0', headTxt: '#1e293b',
      headIntensity: 100,
      headBorderWidth: '2px', headBorderStyle: 'solid',
      headOuterWidth: '0px', headOuterStyle: 'solid',
      bgImageUrl: 'none',
      bgImageOpacity: 50,
      bgImageAutoFit: false,
      bgImageSize: 100,
      bgImagePos: 'center center',
      contentDir: 'column',
      columns: [makeCol('Fecha', 20, 'pat-blank'), makeCol('Notas', 80, 'pat-lines')],
      previewZoom: 100,
    })

    // ─── Secciones colapsables ──────────────────────────────────────
    function toggleSection(key) { sectionOpen[key] = !sectionOpen[key] }

    // ─── Tamaño de hoja ────────────────────────────────────────────
    function onPresetChange() {
      const p = SHEET_SIZES[state.sheetPreset]
      if (p) { state.sheetW = p.w; state.sheetH = p.h }
    }

    // ─── Imagen de fondo ───────────────────────────────────────────
    function handleImageUpload(e) {
      const file = e.target.files[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = ev => { state.bgImageUrl = `url('${ev.target.result}')` }
      reader.readAsDataURL(file)
    }
    function setBgPos(pos) { state.bgImagePos = pos }

    // ─── Columnas ──────────────────────────────────────────────────
    const dragSrc  = ref(null)
    const dragOver = ref(null)

    function addCol() {
      if (state.columns.length < 5) state.columns.push(makeCol('Columna', 25, 'pat-lines'))
    }
    function delCol(idx) { state.columns.splice(idx, 1) }
    function onDragStart(idx) { dragSrc.value = idx }
    function onDragEnd()      { dragSrc.value = null; dragOver.value = null }
    function onDragOver(idx)  { dragOver.value = idx }
    function onDrop(idx) {
      if (dragSrc.value !== null && dragSrc.value !== idx) {
        const [moved] = state.columns.splice(dragSrc.value, 1)
        state.columns.splice(idx, 0, moved)
      }
      dragSrc.value = null; dragOver.value = null
    }

    // ─── CSS vars computadas ────────────────────────────────────────
    const checkboxSvg = computed(() => {
      const c = encodeURIComponent(state.lineColor)
      return `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='25' y='25' width='50' height='50' rx='10' fill='none' stroke='${c}' stroke-width='6'/%3E%3C%2Fsvg%3E")`
    })

    const zoomWrapStyle = computed(() => ({
      width:  (state.sheetW * state.previewZoom / 100) + 'mm',
      height: (state.sheetH * state.previewZoom / 100) + 'mm',
      '--preview-zoom': state.previewZoom / 100,
    }))

    function setZoom(val) {
      state.previewZoom = Math.max(25, Math.min(200, Math.round(val / 5) * 5))
    }

    // El header se compacta al hacer scroll (solo aplica en móvil,
    // donde la página hace scroll de ventana)
    const headerScrolled = ref(false)
    function onWindowScroll() { headerScrolled.value = window.scrollY > 20 }

    // En pantallas pequeñas la hoja al 100% no cabe (1mm ≈ 3.78px):
    // arrancar con un zoom que la muestre completa
    onMounted(() => {
      const availPx = window.innerWidth - 48
      const sheetPx = state.sheetW * 3.78
      if (sheetPx > availPx) setZoom(Math.floor((availPx / sheetPx) * 100))
      onWindowScroll()
      window.addEventListener('scroll', onWindowScroll, { passive: true })
    })

    onBeforeUnmount(() => {
      window.removeEventListener('scroll', onWindowScroll)
    })

    const sheetCssVars = computed(() => ({
      '--sheet-width':       state.sheetW + 'mm',
      '--sheet-height':      state.sheetH + 'mm',
      '--line-spacing':      state.spacing + 'mm',
      '--line-color':        state.lineColor,
      '--line-thickness':    (state.lineThickness * 0.5) + 'px',
      '--grid-bw':           state.gridBorderWidth,
      '--grid-bs':           state.gridBorderStyle,
      '--m-top':             state.mTop + 'mm',
      '--m-bottom':          state.mBot + 'mm',
      '--gutter-x':          state.gutterX + 'mm',
      '--head-size':         state.headHeight + 'mm',
      '--head-bg':           state.headBg,
      '--head-txt':          state.headTxt,
      '--head-int':          state.headIntensity / 100,
      '--head-bw':           state.headBorderWidth,
      '--head-bs':           state.headBorderStyle,
      '--head-ow':           state.headOuterWidth,
      '--head-os':           state.headOuterStyle,
      '--margin-bg':         state.marginColor,
      '--margin-int':        state.marginIntensity / 100,
      '--bg-url':            state.bgImageUrl,
      '--bg-op':             state.bgImageOpacity / 100,
      '--bg-size':           state.bgImageAutoFit ? 'cover' : (state.bgImageSize === 4 ? '4mm' : state.bgImageSize + '%'),
      '--bg-pos':            state.bgImagePos,
      '--check-bg':          checkboxSvg.value,
    }))

    // ─── Imprimir ──────────────────────────────────────────────────
    function doPrint() {
      const mount = printMountRef.value
      const sheet = sheetRef.value
      if (!mount || !sheet) return

      let style = document.getElementById('pl-print-page-style')
      if (!style) { style = document.createElement('style'); style.id = 'pl-print-page-style'; document.head.appendChild(style) }
      style.innerHTML = `@media print { @page { size: portrait; margin: 0; } }`

      mount.innerHTML = ''
      const wrap = document.createElement('div')
      wrap.innerHTML = sheet.outerHTML
      const el = wrap.firstElementChild
      el.classList.add('pl-print-copy')
      mount.appendChild(el)

      document.body.classList.add('pl-printing')
      window.print()
      window.addEventListener('afterprint', () => {
        document.body.classList.remove('pl-printing')
      }, { once: true })
    }

    return {
      BG_POSITIONS,
      sectionOpen, state,
      sheetRef, printMountRef,
      sheetCssVars, zoomWrapStyle, setZoom,
      dragSrc, dragOver,
      toggleSection, onPresetChange, handleImageUpload, setBgPos,
      addCol, delCol, onDragStart, onDragEnd, onDragOver, onDrop,
      doPrint, headerScrolled,
    }
  }
}
</script>

<style scoped>
/* ─── Página ─────────────────────────────────────────────────────── */
.pl-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  font-family: marine, 'Inter', sans-serif;
  background: #f1f5f9;
}

/* ─── Encabezado: barra sticky que se encoge al hacer scroll ────── */
.pl-page-header {
  position: sticky;
  top: 0;
  z-index: 50;
  background: var(--c-primary, #003b4c);
  color: #fff;
  padding: 14px 24px;
  flex-shrink: 0;
  transition: padding 0.28s ease, background 0.28s ease, box-shadow 0.28s ease;
}
.pl-page-header--scrolled {
  background: rgba(0,59,76,0.92);
  backdrop-filter: blur(14px) saturate(1.4);
  -webkit-backdrop-filter: blur(14px) saturate(1.4);
  box-shadow: 0 4px 18px rgba(0,10,14,0.25);
  padding: 7px 24px;
}
.pl-page-header-inner {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 14px;
}
.pl-back-btn {
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.2);
  color: #fff;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background 0.18s;
  font-family: inherit;
}
.pl-back-btn:hover { background: rgba(255,255,255,0.22); }

.pl-title-area { flex: 1; min-width: 0; }
.pl-chip {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255,255,255,0.6);
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.14);
  padding: 4px 12px;
  border-radius: 999px;
}
.pl-heading {
  font-size: 1.35rem;
  font-weight: 700;
  color: #fff;
  margin: 0;
  line-height: 1.2;
}
.pl-desc {
  font-size: 0.82rem;
  color: rgba(255,255,255,0.65);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 1.6em;
  transition: max-height 0.28s ease, opacity 0.2s ease;
}
.pl-page-header--scrolled .pl-desc { max-height: 0; opacity: 0; }

/* ─── Wrapper del card ───────────────────────────────────────────── */
.pl-body-wrap {
  flex: 1;
  min-height: 0;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
}

/* ─── Card principal (aside + main) ─────────────────────────────── */
.pl-page-body {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  max-width: 1120px;
  margin: 0 auto;
  width: 100%;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 8px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.05);
}

/* ─── Panel lateral ─────────────────────────────────────────────── */
.pl-aside {
  width: 340px;
  min-width: 280px;
  min-height: 0;
  background: #fff;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 4px 0 12px -3px rgba(0,0,0,0.08);
}
.pl-panel-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.pl-panel-content::-webkit-scrollbar { display: none; }

/* ─── Secciones ─────────────────────────────────────────────────── */
.pl-section { border-bottom: 1px solid #f1f5f9; padding-bottom: 4px; }
.pl-sec-head {
  font-size: 0.72rem;
  text-transform: uppercase;
  font-weight: 700;
  color: var(--c-primary, #003b4c);
  padding: 10px 0 6px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  user-select: none;
  letter-spacing: 0.05em;
}
.pl-sec-head:hover { color: var(--c-coral, #ed8f7b); }
.pl-arrow { transition: transform 0.2s; display: inline-block; font-size: 0.7rem; }
.pl-arrow.up { transform: rotate(-90deg); }
.pl-sec-body { padding-bottom: 10px; }

/* ─── Controles ─────────────────────────────────────────────────── */
.pl-ctrl { margin-bottom: 10px; }
.pl-label {
  display: flex;
  justify-content: space-between;
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 4px;
}
.pl-sublabel {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--c-primary, #003b4c);
  margin: 10px 0 4px;
}
.pl-val { color: var(--c-coral, #ed8f7b); font-family: monospace; }
.pl-help { font-size: 0.72rem; color: #94a3b8; font-style: italic; margin: 4px 0 0; text-align: center; }

.pl-select {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 0.82rem;
  font-family: inherit;
  color: #334155;
  background: #fff;
}
.pl-input {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 0.82rem;
  font-family: inherit;
  color: #334155;
  background: #fff;
}
.pl-input--flex { flex: 1; width: auto; }
.pl-input--w60  { width: 60px; flex-shrink: 0; }
.pl-range { width: 100%; accent-color: var(--c-coral, #ed8f7b); cursor: pointer; }
.pl-color { width: 100%; height: 32px; padding: 2px; border: 1px solid #cbd5e1; border-radius: 6px; cursor: pointer; }
.pl-color--sm { width: 32px; height: 22px; padding: 1px; }

.pl-mini-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.pl-info-box {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  padding: 8px;
}
.pl-info-row { display: flex; justify-content: space-between; align-items: center; }
.pl-autofit-box {
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 6px;
  padding: 8px;
  margin: 8px 0;
}
.pl-check-label { display: flex; align-items: center; gap: 6px; font-size: 0.82rem; color: #15803d; font-weight: 600; cursor: pointer; }
.pl-file-input { width: 100%; font-size: 0.78rem; margin-bottom: 10px; }
.pl-disabled { opacity: 0.4; pointer-events: none; filter: grayscale(1); }

/* ─── Posición de imagen ─────────────────────────────────────────── */
.pl-pos-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px; margin-top: 6px; }
.pl-pos-btn {
  background: #f1f5f9; border: 1px solid #cbd5e1; border-radius: 4px;
  height: 26px; cursor: pointer; position: relative;
}
.pl-pos-btn.active { background: var(--c-primary, #003b4c); border-color: var(--c-primary, #003b4c); }
.pl-pos-btn::after {
  content: ''; position: absolute;
  width: 5px; height: 5px; background: #94a3b8; border-radius: 50%;
}
.pl-pos-btn.active::after { background: white; }
.p-tl::after { top: 4px; left: 4px; }      .p-tc::after { top: 4px; left: 50%; translate: -50%; }    .p-tr::after { top: 4px; right: 4px; }
.p-cl::after { top: 50%; left: 4px; translate: 0 -50%; } .p-cc::after { top: 50%; left: 50%; translate: -50% -50%; width: 7px; height: 7px; } .p-cr::after { top: 50%; right: 4px; translate: 0 -50%; }
.p-bl::after { bottom: 4px; left: 4px; }   .p-bc::after { bottom: 4px; left: 50%; translate: -50%; } .p-br::after { bottom: 4px; right: 4px; }

/* ─── Columnas ───────────────────────────────────────────────────── */
.pl-col-list { display: flex; flex-direction: column; gap: 8px; margin-bottom: 10px; }
.pl-col-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px;
  transition: opacity 0.15s, border-color 0.15s;
}
.pl-col-card.col-dragging { opacity: 0.35; }
.pl-col-card.col-over { border-color: var(--c-coral, #ed8f7b); border-style: dashed; background: #fff5f5; }
.pl-col-head { display: flex; gap: 6px; align-items: center; margin-bottom: 8px; }
.pl-drag-handle { cursor: grab; color: #94a3b8; font-size: 1.1rem; flex-shrink: 0; }
.pl-drag-handle:active { cursor: grabbing; }
.pl-btn-del {
  background: #fee2e2; color: #ef4444; border: none;
  width: 24px; border-radius: 4px; cursor: pointer; font-weight: bold; flex-shrink: 0; font-size: 1rem;
}
.pl-col-body-row { display: grid; grid-template-columns: 1fr auto; gap: 8px; }
.pl-btn-add {
  width: 100%;
  border: 2px dashed #cbd5e1;
  background: transparent;
  color: #64748b;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.82rem;
  font-family: inherit;
}
.pl-btn-add:hover { border-color: var(--c-coral, #ed8f7b); color: var(--c-coral, #ed8f7b); }

/* ─── Sección de impresión ───────────────────────────────────────── */
.pl-print-section {
  background: #1e293b;
  padding: 1.25rem;
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
}
.pl-btn-print {
  width: 100%;
  background: var(--c-primary, #003b4c);
  color: #fff;
  border: none;
  padding: 11px;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
  font-size: 0.88rem;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.18s;
}
.pl-btn-print:hover { background: #004f67; }

/* ─── Área de preview ────────────────────────────────────────────── */
.pl-main {
  flex: 1;
  background: #cbd5e1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  min-height: 0;
}
.pl-sheet-scroll {
  flex: 1;
  overflow: auto;
  display: flex;
  padding: 2rem;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
}

/* ─── Zoom toolbar ───────────────────────────────────────────────── */
.pl-zoom-bar {
  position: absolute;
  top: 12px;
  right: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(30, 41, 59, 0.92);
  color: #fff;
  border-radius: 999px;
  padding: 4px;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}
.pl-zoom-btn {
  width: 28px; height: 28px;
  background: transparent;
  color: #fff;
  border: none;
  border-radius: 50%;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-family: inherit;
  line-height: 1;
  transition: background 0.15s, opacity 0.15s;
}
.pl-zoom-btn:not(:disabled):hover { background: rgba(255,255,255,0.15); }
.pl-zoom-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.pl-zoom-btn.pl-zoom-reset { font-size: 0.78rem; }
.pl-zoom-val {
  min-width: 44px;
  text-align: center;
  font-size: 0.78rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  user-select: none;
}

/* ─── Wrapper de zoom ────────────────────────────────────────────── */
.pl-sheet-zoom {
  position: relative;
  flex-shrink: 0;
  /* margin auto centra la hoja sin recortarla cuando excede el área scrolleable */
  margin: auto;
}
.pl-sheet-zoom > .pl-sheet {
  position: absolute;
  top: 0; left: 0;
  transform: scale(var(--preview-zoom, 1));
  transform-origin: 0 0;
}

/* ─── Hoja ───────────────────────────────────────────────────────── */
.pl-sheet {
  width: var(--sheet-width);
  height: var(--sheet-height);
  min-width: var(--sheet-width);
  min-height: var(--sheet-height);
  position: relative;
  box-shadow: 0 10px 30px -5px rgba(0,0,0,0.22);
  padding: var(--m-top) 10mm var(--m-bottom) 10mm;
  flex-shrink: 0;
}
.pl-sheet::before {
  content: '';
  position: absolute;
  inset: 0;
  background-color: var(--margin-bg);
  opacity: var(--margin-int);
  z-index: 0;
}
.pl-write-area {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  background: white;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-width: var(--grid-bw);
  border-style: var(--grid-bs);
  border-color: var(--line-color);
  transform: translateX(var(--gutter-x));
}
.pl-bg-img {
  position: absolute;
  inset: 0;
  background-image: var(--bg-url);
  background-repeat: no-repeat;
  background-size: var(--bg-size);
  background-position: var(--bg-pos);
  opacity: var(--bg-op);
  pointer-events: none;
  z-index: 1;
}
.pl-grid-layout {
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
  display: flex;
}

/* ─── Header (vertical = col, horizontal = row) ──────────────────── */
.pl-grid-layout.is-col { flex-direction: column; }
.pl-grid-layout.is-row { flex-direction: row; }

.pl-row-header {
  flex-shrink: 0;
  display: flex;
  position: relative;
  z-index: 5;
  border-color: var(--line-color);
}
.pl-row-header::before {
  content: '';
  position: absolute;
  inset: 0;
  background-color: var(--head-bg);
  opacity: var(--head-int);
  z-index: -1;
}

.is-col .pl-row-header {
  width: 100%;
  flex-direction: row;
  flex-basis: var(--head-size);
  border-bottom-width: var(--head-bw); border-bottom-style: var(--head-bs);
  border-top-width: var(--head-ow);    border-top-style: var(--head-os);
  border-left-width: var(--head-ow);   border-left-style: var(--head-os);
  border-right-width: var(--head-ow);  border-right-style: var(--head-os);
}
.is-row .pl-row-header {
  height: 100%;
  flex-direction: column;
  flex-basis: var(--head-size);
  border-right-width: var(--head-bw);  border-right-style: var(--head-bs);
  border-top-width: var(--head-ow);    border-top-style: var(--head-os);
  border-bottom-width: var(--head-ow); border-bottom-style: var(--head-os);
  border-left-width: var(--head-ow);   border-left-style: var(--head-os);
}

.pl-cell-head {
  flex: 1;
  font-size: 9pt;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  overflow: hidden;
  border-right: 1px solid var(--line-color);
  color: var(--head-txt);
  padding: 2px;
}
.pl-cell-head:last-child { border: none; }
.is-row .pl-cell-head {
  border-right: none;
  border-bottom: 1px solid var(--line-color);
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
}

.pl-row-body { flex: 1; display: flex; z-index: 4; }
.is-col .pl-row-body { flex-direction: row; }
.is-row .pl-row-body { flex-direction: column; }

.pl-cell-body {
  flex: 1;
  border-color: var(--line-color);
  border-style: solid;
  border-width: 0;
  background-position: 0 0;
}
.is-col .pl-cell-body { border-right-width: 1px; }
.is-row .pl-cell-body { border-bottom-width: 1px; }
.pl-cell-body:last-child { border: none; }

/* ─── Patrones ───────────────────────────────────────────────────── */
.is-col .pl-cell-body.pat-lines {
  background-image: linear-gradient(
    to bottom,
    transparent calc(100% - var(--line-thickness)),
    var(--line-color) var(--line-thickness)
  );
  background-size: 100% var(--line-spacing);
}
.is-row .pl-cell-body.pat-lines {
  background-image: linear-gradient(
    to left,
    transparent calc(100% - var(--line-thickness)),
    var(--line-color) var(--line-thickness)
  );
  background-size: var(--line-spacing) 100%;
}
.pl-cell-body.pat-grid {
  background-image:
    linear-gradient(to right, transparent calc(100% - var(--line-thickness)), var(--line-color) var(--line-thickness)),
    linear-gradient(to bottom, transparent calc(100% - var(--line-thickness)), var(--line-color) var(--line-thickness));
  background-size: var(--line-spacing) var(--line-spacing);
}
.pl-cell-body.pat-dots {
  background-image: radial-gradient(circle, var(--line-color) 1px, transparent 1px);
  background-size: var(--line-spacing) var(--line-spacing);
  background-position: center center;
}
.pl-cell-body.pat-check {
  background-image: var(--check-bg);
  background-repeat: repeat-y;
  background-position: center 0;
  background-size: var(--line-spacing) var(--line-spacing);
}
.pl-cell-body.pat-blank { background: none; }

/* ─── Print ──────────────────────────────────────────────────────── */
.pl-print-mount { display: none; }

@media print {
  /* Liberar el viewport: la página debe poder crecer al alto total de las copias */
  .pl-page {
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
    background: #fff !important;
    display: block !important;
  }
  .pl-page-header { display: none !important; }
  .pl-body-wrap   { display: none !important; }

  .pl-print-mount {
    display: block !important;
    width: 100%;
    padding: 0;
    margin: 0;
  }
  .pl-print-mount > .pl-sheet {
    box-shadow: none !important;
    margin: 0 auto;
    page-break-inside: avoid;
    break-inside: avoid;
    page-break-after: always;
    break-after: page;
  }
  .pl-print-mount > .pl-sheet:last-child {
    page-break-after: auto;
    break-after: auto;
  }
}

/* ─── Tablet (769px – 1023px) ────────────────────────────────────── */
@media (max-width: 1023px) {
  .pl-aside { width: 280px; min-width: 240px; }
}

/* ─── Mobile ≤ 768px ─────────────────────────────────────────────── */
@media (max-width: 768px) {
  /* Release viewport lock — page scrolls naturally on mobile */
  .pl-page { height: auto; min-height: 100vh; overflow: auto; }

  /* Header */
  .pl-page-header { padding: 9px 16px; }
  .pl-page-header--scrolled { padding: 6px 16px; }
  .pl-heading { font-size: 1.05rem; }
  .pl-desc, .pl-chip { display: none; }
  .pl-back-btn { width: 38px; height: 38px; font-size: 0.85rem; }

  /* Body card stacks vertically */
  .pl-body-wrap { flex: none; padding: 0.5rem; }
  .pl-page-body { flex: none; flex-direction: column; overflow: visible; border-radius: 12px; }

  /* Aside full-width, page scrolls (no height cap) */
  .pl-aside { width: 100%; min-height: 0; overflow: visible; border-right: none; border-bottom: 1px solid #e2e8f0; }
  .pl-panel-content { overflow-y: visible; }

  /* Controls fit smaller screens */
  .pl-mini-grid { grid-template-columns: 1fr 1fr; }
  .pl-col-body-row { grid-template-columns: 1fr auto; }

  /* Print section on mobile */
  .pl-print-section { padding: 1rem; }
  .pl-btn-print { flex: 1; min-width: 120px; }

  /* Preview area */
  .pl-main { min-height: 60vh; }
  .pl-sheet-scroll { padding: 1rem; }

  /* 16px evita el auto-zoom de iOS al enfocar inputs */
  .pl-select, .pl-input { font-size: 16px; }

  /* Objetivos táctiles más grandes */
  .pl-pos-btn { height: 34px; }
  .pl-btn-del { width: 32px; height: 32px; }
  .pl-zoom-btn { width: 34px; height: 34px; }
}

/* ─── Small phones ≤ 480px ───────────────────────────────────────── */
@media (max-width: 480px) {
  .pl-body-wrap { padding: 0.375rem; }
  .pl-page-body { border-radius: 10px; }
  .pl-panel-content { padding: 0.75rem; }
  .pl-print-section { padding: 0.75rem; }
  .pl-btn-print { font-size: 0.8rem; padding: 9px; }
  .pl-sheet-scroll { padding: 0.75rem; }
}
</style>
